/**
 * Password Reset Page
 * Unified password reset flow for all roles
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Heart, ArrowLeft, Mail, Phone, Lock, CheckCircle, KeyRound
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { Toaster } from '../components/Toaster';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input for
// the OTP send leg of the reset flow.
import { PhoneInput } from '../components/ui/phone-input';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const roleHint = searchParams.get('role') || 'patient';

  const [step, setStep] = useState(1); // 1: Enter email/phone, 2: Enter code, 3: New password, 4: Success
  const [resetMethod, setResetMethod] = useState('email'); // 'email' or 'phone'
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Mock sending code
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: 'Verification Code Sent',
      description: resetMethod === 'email' 
        ? `Code sent to ${email}` 
        : `Code sent to ${phone}`
    });
    
    setLoading(false);
    setStep(2);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Mock verification - any 6-digit code works
    await new Promise(resolve => setTimeout(resolve, 500));
    
    if (verificationCode.length === 6) {
      setStep(3);
    } else {
      toast({
        title: 'Invalid Code',
        description: 'Please enter a valid 6-digit code',
        variant: 'destructive'
      });
    }
    
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Passwords Do Not Match',
        description: 'Please ensure both passwords are identical',
        variant: 'destructive'
      });
      return;
    }
    
    if (newPassword.length < 6) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }
    
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setStep(4);
    setLoading(false);
  };

  const getBackPath = () => {
    switch (roleHint) {
      case 'clinic': return '/clinic/login';
      case 'provider_coordinator': return '/coordinator/login';
      case 'representative': return '/clinic/representative/login';
      case 'treatx-admin': return '/treatx-admin/login';
      default: return '/login';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Toaster />
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-14 h-14 bg-gradient-to-br from-rose-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
            {step === 4 ? (
              <CheckCircle className="h-7 w-7 text-white" />
            ) : (
              <KeyRound className="h-7 w-7 text-white" />
            )}
          </div>
          <CardTitle className="text-2xl">
            {step === 4 ? t('patient.resetPassword.successTitle') : t('patient.resetPassword.title')}
          </CardTitle>
          <CardDescription>
            {step === 1 && t('patient.auth.forgotBody')}
            {step === 2 && t('patient.auth.verifyEmailBody')}
            {step === 3 && t('patient.resetPassword.subtitle')}
            {step === 4 && t('patient.resetPassword.successBody')}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Step 1: Enter Email/Phone */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-4">
              {/* Method Toggle */}
              <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => setResetMethod('email')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    resetMethod === 'email' 
                      ? 'bg-white shadow-sm text-slate-800' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Mail className="h-4 w-4 inline mr-1" />
                  {t('patient.auth.email')}
                </button>
                <button
                  type="button"
                  onClick={() => setResetMethod('phone')}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    resetMethod === 'phone' 
                      ? 'bg-white shadow-sm text-slate-800' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Phone className="h-4 w-4 inline mr-1" />
                  {t('patient.register.phone')}
                </button>
              </div>

              {resetMethod === 'email' ? (
                <div className="space-y-2">
                  <Label htmlFor="email">{t('patient.auth.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('patient.register.phone')}</Label>
                  <PhoneInput
                    value={phone}
                    onChange={(e164) => setPhone(e164)}
                    testId="reset-phone-input"
                    placeholder={t('patient.register.phone')}
                    required
                  />
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-rose-600 to-purple-600"
                disabled={loading || (resetMethod === 'email' ? !email : !phone)}
              >
                {loading ? t('patient.auth.signingIn') : t('patient.auth.forgotSubmit')}
              </Button>
            </form>
          )}

          {/* Step 2: Enter Verification Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('patient.auth.verifyEmailTitle')}</Label>
                <Input
                  id="code"
                  type="text"
                  placeholder={t('patient.common.submit')}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-rose-600 to-purple-600"
                disabled={loading || verificationCode.length !== 6}
              >
                {loading ? t('patient.resetPassword.submitting') : t('patient.common.confirm')}
              </Button>

              <Button 
                type="button" 
                variant="ghost"
                className="w-full"
                onClick={() => setStep(1)}
              >
                {t('patient.register.didNotReceive')}
              </Button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t('patient.resetPassword.newPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t('patient.resetPassword.confirmPassword')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-rose-600 to-purple-600"
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? t('patient.resetPassword.submitting') : t('patient.resetPassword.submit')}
              </Button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-slate-600">
                {t('patient.resetPassword.successBody')}
              </p>
              <Button 
                className="w-full bg-gradient-to-r from-rose-600 to-purple-600"
                onClick={() => navigate(getBackPath())}
              >
                {t('patient.resetPassword.goToSignIn')}
              </Button>
            </div>
          )}

          {/* Back Link */}
          {step !== 4 && (
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={() => navigate(getBackPath())}
                className="text-slate-500"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('patient.common.back')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
